import { BlockAccessPointDto, BlockSide, ConfigAreaDto } from '@yard/app/api'
import {
  BAY_WIDTH,
  BayGap,
  ROW_HEIGHT,
  baySpanExtraPx,
  buildBayGaps,
  gapPxBefore,
} from './yard-view.utils'

export const ACCESS_POINT_BAR_THICKNESS = 20

/** Gap between the bar and the block boundary so the bar does not touch the block edge. */
export const ACCESS_POINT_BAR_GAP = 3

/**
 * Width of the row-header column (left of the bay grid).
 * Matches the minWidth used in YardBlockRowHeader cells.
 */
const ROW_HEADER_WIDTH = BAY_WIDTH

/**
 * Height of the bay-header row (above the row grid).
 * Matches the height used in YardBlockBayHeader cells.
 */
const BAY_HEADER_HEIGHT = ROW_HEIGHT

export interface AccessPointBarRect {
  x: number
  y: number
  width: number
  height: number
  resolvedMin: number
  resolvedMax: number
  /** True when the access point covers the full face (min/max both null) */
  isFullFace: boolean
  side: BlockSide
}

/** Convenience wrapper that produces the BayGap[] consumed by `resolveAccessPointBarRect`. */
export function getBayGapInfo(configAreas: ConfigAreaDto[] | undefined, maxBays: number): BayGap[] {
  return buildBayGaps(configAreas, maxBays)
}

/**
 * Computes the absolute position and size of a face-aligned bar for an access point.
 * Coordinates are relative to the top-left corner of the YardBlockNode content area
 * (including row and bay headers). Bars protrude outside the block boundary.
 *
 * Null min is resolved to 1; null max is resolved to maxBays / maxRows.
 *
 * When `bayGaps` is provided, x positions and widths are adjusted to account for
 * inter-bay gap columns (reefer plugs and space-between-bays) so that bars align
 * with the actual visual bay positions.
 */
export function resolveAccessPointBarRect(
  accessPoint: Pick<BlockAccessPointDto, 'side' | 'minBay' | 'maxBay' | 'minRow' | 'maxRow'>,
  block: { maxBays: number; maxRows: number },
  bayGaps?: BayGap[],
): AccessPointBarRect {
  const { side, minBay, maxBay, minRow, maxRow } = accessPoint
  const { maxBays, maxRows } = block
  const t = ACCESS_POINT_BAR_THICKNESS
  const g = ACCESS_POINT_BAR_GAP

  const gaps = bayGaps ?? []

  if (side === 'North' || side === 'South') {
    const resolvedMin = minBay ?? 1
    const resolvedMax = maxBay ?? maxBays
    const isFullFace = minBay == null && maxBay == null
    const x = ROW_HEADER_WIDTH + (resolvedMin - 1) * BAY_WIDTH + gapPxBefore(resolvedMin, gaps)
    const width =
      (resolvedMax - resolvedMin + 1) * BAY_WIDTH +
      baySpanExtraPx(resolvedMin, resolvedMax - resolvedMin + 1, gaps)
    const y = side === 'North' ? -(t + g) : BAY_HEADER_HEIGHT + maxRows * ROW_HEIGHT + g

    return { x, y, width, height: t, resolvedMin, resolvedMax, isFullFace, side }
  }

  // East or West
  const resolvedMin = minRow ?? 1
  const resolvedMax = maxRow ?? maxRows
  const isFullFace = minRow == null && maxRow == null
  const y = BAY_HEADER_HEIGHT + (resolvedMin - 1) * ROW_HEIGHT
  const height = (resolvedMax - resolvedMin + 1) * ROW_HEIGHT
  // East offset = total bay-axis pixels including every gap before edge maxBays + 1.
  const x =
    side === 'East'
      ? ROW_HEADER_WIDTH + maxBays * BAY_WIDTH + gapPxBefore(maxBays + 1, gaps) + g
      : -(t + g)

  return { x, y, width: t, height, resolvedMin, resolvedMax, isFullFace, side }
}
